import React, { useState, useEffect, useRef } from 'react';
import CompanyOverviewTable from '../../components/CompanyOverviewTable/CompanyOverviewTable';
import styles from './CompanyOverview.module.css';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DashboardConfiguration from '../../components/DashboardConfiguration/DashboardConfiguration';
import RadioGroupWithDelete from '../../components/RadioGroupwithDelete/RadioGroupwithDelete';
import EditableField from '../../components/EditableField/EditableField';
import DownloadButton from '../../components/DownloadButton/DownloadButton';
import useDashboardQuery from '../../hooks/useDashboardQuery';
import useProfileQuery from '../../hooks/useProfileQuery';
import keycloak from '../../keycloak';

const columns = [
  'date',
  'invoiceID',
  'supplierEmail',
  'expenseType',
  'category',
  'originalAmount',
  'status',
  'reimbursed',
  'approverEmails',
  'GST',
  'PST',
  'distance',
  'project'
];

const columnsMap = {
  date: 'Date',
  invoiceID: '#',
  supplierEmail: 'Submitted By',
  expenseType: 'Expense Type',
  category: 'Category',
  originalAmount: 'Sub Total ($)',
  status: 'Status',
  reimbursed: 'Paid',
  approverEmails: 'Assigned To',
  GST: 'GST ($)',
  PST: 'PST ($)',
  distance: 'Distance',
  project: 'Project',
};

export default function CompanyOverview() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [draftSelectedColumns, setDraftSelectedColumns] = useState(columns);
  const [activeTab, setActiveTab] = useState('All'); // 'All', 'Approved', 'Unapproved'
  const [selectedConfigOption, setSelectedConfigOption] = useState('List All');
  const [draftSelectedConfigOption, setDraftSelectedConfigOption] = useState('List All');
  const [configName, setConfigName] = useState('');
  const [viewBy, setViewBy] = useState('currentPeriod');
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnOrder, setColumnOrder] = useState(columns);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  const searchInputRef = useRef(null);
  const columnPickerRef = useRef(null);
  const { data, refetch } = useDashboardQuery();
  const { updateMyProfile } = useProfileQuery();
  const email = keycloak.tokenParsed?.email;

  const [configOptions, setConfigOptions] = useState([]);
  const [configColumnMapping, setConfigColumnMapping] = useState({ 'List All': columns });

  const canShowReimbursedColumn = activeTab === 'Approved';
  const availableColumnsForTab = canShowReimbursedColumn
    ? columns
    : columns.filter((column) => column !== 'reimbursed');

  // --- Tab handlers ---
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    console.log(tab.charAt(0).toUpperCase() + tab.slice(1));
  };

  // --- Config handlers ---
  const handleSelectConfigOption = (option) => {
    setDraftSelectedConfigOption(option);
    setDraftSelectedColumns(configColumnMapping[option] || []);
  };

  const handleCheckboxChange = (value) => {
    if (draftSelectedColumns.includes(value)) {
      setDraftSelectedColumns(draftSelectedColumns.filter((checkbox) => checkbox !== value));
    } else {
      setDraftSelectedColumns([...draftSelectedColumns, value]);
    }
  };

  const handleViewByChange = (event) => setViewBy(event.target.value);
  const openModal = () => {
    setDraftSelectedColumns(selectedColumns);
    setDraftSelectedConfigOption(selectedConfigOption);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);
  const handleconfigChange = (value) => setConfigName(value || '');

  const handleSave = async () => {
    const typedName = configName.trim();
    // No new name typed while an existing (non-default) config is selected: update it in place.
    const isEditingExisting = !typedName && draftSelectedConfigOption && draftSelectedConfigOption !== 'List All';
    const targetName = isEditingExisting ? draftSelectedConfigOption : typedName;

    if (!targetName) {
      alert('Please enter a configuration name before saving.');
      return;
    }
    const updatedConfigOptions = configOptions.includes(targetName)
      ? configOptions
      : [...configOptions, targetName];
    const updatedMapping = { ...configColumnMapping, [targetName]: draftSelectedColumns };

    setConfigOptions(updatedConfigOptions);
    setConfigColumnMapping(updatedMapping);
    setDraftSelectedConfigOption(targetName);
    setConfigName('');

    try {
      await updateMyProfile(email, { dashboardCfg: JSON.stringify(updatedMapping) });
      refetch();
    } catch (error) {
      console.error("Error updating dashboard configuration:", error);
    }

  };

  const handleApply = () => {
    setSelectedColumns(draftSelectedColumns);
    setSelectedConfigOption(draftSelectedConfigOption);
    closeModal();
    setConfigName('');
  };

  const deleteConfigOption = async (optionToDelete) => {
    const updatedConfigOptions = configOptions.filter(option => option !== optionToDelete);
    const updatedMapping = Object.fromEntries(
      Object.entries(configColumnMapping).filter(([key]) => key !== optionToDelete)
    );

    setConfigOptions(updatedConfigOptions);
    setConfigColumnMapping(updatedMapping);

    try {
      await updateMyProfile(email, { dashboardCfg: JSON.stringify(updatedMapping) });
      refetch();
    } catch (error) {
      console.error("Error updating dashboard configuration:", error);
    }
  };

  const displayedColumnOrder = columnOrder.filter(
    (key) => selectedColumns.includes(key) && availableColumnsForTab.includes(key)
  );

  const moveDisplayedColumn = (fromIndex, toIndex) => {
    if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;
    if (toIndex < 0 || toIndex >= displayedColumnOrder.length) return;
    const visible = [...displayedColumnOrder];
    const [moved] = visible.splice(fromIndex, 1);
    visible.splice(toIndex, 0, moved);
    const hidden = columnOrder.filter((key) => !visible.includes(key));
    setColumnOrder([...visible, ...hidden]);
  };

  // --- Load user dashboard configuration ---
  useEffect(() => {
    if (data?.getUserProfile?.body?.dashboardCfg) {
        try {
            const cfg = data.getUserProfile.body.dashboardCfg;

            // Only parse if it looks like JSON
            const parsedDashCfg = cfg.startsWith("{") ? JSON.parse(cfg) : { 'List All': columns };

            setConfigColumnMapping(parsedDashCfg);
            setConfigOptions(Object.keys(parsedDashCfg));
        } catch (error) {
            console.error("Failed to parse dashboard configuration:", error);
            setConfigColumnMapping({ 'List All': columns });
            setConfigOptions(['List All']);
        }
    }
}, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(event.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Company Overview</h2>
        <div className={styles.downloadContainer}>
          <DownloadButton
            label={"PDF"}
            viewBy={viewBy}
            activeTab={activeTab}
            useActiveTab
          />
          <DownloadButton
            label={"CSV"}
            viewBy={viewBy}
            activeTab={activeTab}
            useActiveTab
          />
          <DownloadButton
            label={"CSV"}
            viewBy={viewBy}
            activeTab={activeTab}
            useActiveTab
            reportColumns={displayedColumnOrder}
            globalFilter={globalFilter}
            buttonText="Download View as CSV"
          />
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <div className={styles.statusContainer}>
          <div className={styles.statusRow}> 
            <button 
              onClick={() => handleTabChange('All')} 
              className={`${styles.statusButton} ${activeTab === 'All' ? styles.selected : styles.notSelected}`}>
              All
            </button>
          </div>
          <div className={styles.statusRow}> 
            <button 
              onClick={() => handleTabChange('Unapproved')} 
              className={`${styles.statusButton} ${activeTab === 'Unapproved' ? styles.selected : styles.notSelected}`}>
              Unapproved
            </button>
          </div> 
          <div className={styles.statusRow}> 
            <button 
              onClick={() => handleTabChange('Approved')} 
              className={`${styles.statusButton} ${activeTab === 'Approved' ? styles.selected : styles.notSelected}`}>
              Approved
            </button>
          </div>
        </div>
        <div className={styles.controlsContainer}>
          <div className={styles.dropdownContainer}>
            <select id="dropdown" className={styles.dropdown} value={viewBy} onChange={handleViewByChange}>
              <option value="currentPeriod">View By: Current Period</option>
              <option value="currentMonth">View By: Current Month</option>
              <option value="currentYear">View By: Current Year</option>
              <option value="all">View By: All</option>
            </select>
          </div>
          <button
            className={styles.externalControlButton}
            onClick={() => setShowColumnFilters((prev) => !prev)}
          >
            <TuneIcon fontSize="small" />
            {showColumnFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          {showSearchInput ? (
            <input
              ref={searchInputRef}
              className={styles.searchInlineInput}
              type="text"
              placeholder="Search invoices..."
              value={globalFilter || ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              onBlur={() => {
                if (!globalFilter) setShowSearchInput(false);
              }}
            />
          ) : (
            <button
              className={styles.externalControlButton}
              onClick={() => {
                setShowSearchInput(true);
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }}
            >
              <SearchIcon fontSize="small" />
              Search
            </button>
          )}
          <div className={styles.columnPickerAnchor} ref={columnPickerRef}>
            <button
              className={styles.externalControlButton}
              onClick={() => setShowColumnPicker((prev) => !prev)}
            >
              Columns
            </button>
            {showColumnPicker && (
              <div className={styles.columnPicker}>
                <button
                  type="button"
                  className={styles.resetOrderButton}
                  onClick={() => setColumnOrder(columns)}
                >
                  Reset Order
                </button>
                <div className={styles.columnList}>
                  {displayedColumnOrder.map((key, index) => (
                    <div
                      key={key}
                      className={styles.columnOption}
                      draggable
                      onDragStart={() => setDraggedColumnIndex(index)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        moveDisplayedColumn(draggedColumnIndex, index);
                        setDraggedColumnIndex(null);
                      }}
                      onDragEnd={() => setDraggedColumnIndex(null)}
                    >
                      <div className={styles.columnLabelWrap}>
                        <DragIndicatorIcon className={styles.dragHandle} />
                        <span className={styles.columnName}>{columnsMap[key] || key}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={styles.iconContainer}>
            <EditNoteIcon
              className={styles.editIcon}
              fontSize='large'
              onClick={openModal}
            />
          </div>
        </div>
      </div>

      {/* --- Table --- */}
      <CompanyOverviewTable
        selectedColumns={selectedColumns}
        activeTab={activeTab}
        viewBy={viewBy}
        showColumnFilters={showColumnFilters}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        columnOrder={columnOrder}
      />

      {/* --- Modal --- */}
      {isModalOpen && (
        <div className={styles.modalContainer}>
          <DashboardConfiguration title="Dashboard Configuration" onClose={closeModal}>
            <div>
              Select existing configuration:
              <div className={styles.selectConfig}>
                <RadioGroupWithDelete
                  options={configOptions}
                  selectedOption={draftSelectedConfigOption}
                  onChange={handleSelectConfigOption}
                  handleDeletion={deleteConfigOption} 
                />
              </div>
            </div>

            <div className={styles.configName}>
              Configuration Name:
              <div style={{ width: "200px" }}>
                <EditableField isEditing={true} onChange={handleconfigChange} initialData={configName} />
              </div>
            </div>

            <div className={styles.modalContent}>
              <div className={styles.configCheckbox}>
                {availableColumnsForTab.map((value, index) => (
                  <div key={index} className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id={`checkbox${index}`}
                      value={value}
                      checked={draftSelectedColumns.includes(value)}
                      onChange={() => handleCheckboxChange(value)}
                    />
                    <label htmlFor={`checkbox${index}`}>{value}</label>
                  </div>
                ))}
              </div>
              <div className={styles.buttonGroup}>
                <button className={styles.saveButton} onClick={handleSave}>Save</button>
                <button className={styles.applyButton} onClick={handleApply}>Apply</button>
              </div>
            </div>
          </DashboardConfiguration>
        </div>
      )}
    </div>
  );
}
